import { Link } from "react-router-dom"

import { cn } from "@/lib/utils"
import { getDifficultyLabel } from "@/lib/academy/difficulty"
import { formatDuration, formatProgressPercent } from "@/lib/academy/format"
import { getCourseCoverUrl } from "@/lib/course-covers"

// El nivel es una etiqueta neutra con un punto de color, no una píldora tricolor.
const DIFFICULTY_DOT = {
  beginner: "bg-emerald-400",
  intermediate: "bg-amber-400",
  advanced: "bg-rose-400",
} as const

export interface CourseCardCourse {
  id: string
  title: string
  slug: string
  thumbnail_url?: string | null
  instructor_name?: string | null
  difficulty_level?: string | null
  category?: string | null
  lesson_count?: number | null
  student_count?: number | null
  estimated_hours?: number | null
}

export interface CourseCardEnrollment {
  completedLessons: number
  totalLessons: number
}

// Portada, título, una sola línea de dato y el nivel. Con sesión e inscripción la
// línea es el avance ("3/12 clases"); sin ella, el tamaño del curso. El instructor,
// los estudiantes, la categoría y el temario viven en el detalle del curso.
export function CourseCard({
  course,
  enrollment,
}: {
  course: CourseCardCourse
  enrollment?: CourseCardEnrollment
}) {
  const difficultyLabel = getDifficultyLabel(course.difficulty_level)
  const progressPercent = enrollment
    ? formatProgressPercent(enrollment.completedLessons, enrollment.totalLessons)
    : null
  const duration = formatDuration(course.estimated_hours)

  const meta = enrollment
    ? `${enrollment.completedLessons}/${enrollment.totalLessons} clases`
    : course.lesson_count
      ? `${course.lesson_count} ${course.lesson_count === 1 ? "clase" : "clases"}`
      : duration

  return (
    <Link
      to={`/courses/${course.slug}`}
      className="group flex flex-col gap-3 rounded-2xl outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-foreground/5 ring-1 ring-foreground/10">
        <img
          src={getCourseCoverUrl(course)}
          alt=""
          className="h-full w-full object-cover transition-transform duration-(--dur-slow) ease-(--ease-out-expo) group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        />
        {progressPercent !== null && progressPercent > 0 && (
          <div
            role="progressbar"
            aria-label={`Avance en ${course.title}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
            className="absolute inset-x-0 bottom-0 h-1 bg-black/40"
          >
            <div
              className="motion-progress motion-progress-in h-full w-full bg-primary"
              style={{ transform: `scaleX(${progressPercent / 100})` }}
            />
          </div>
        )}
      </div>

      <div className="px-1">
        <h3 className="line-clamp-2 text-lg font-semibold leading-snug tracking-tight text-foreground">
          {course.title}
        </h3>
        {(meta || difficultyLabel) && (
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">{meta}</p>
            {difficultyLabel && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-foreground/10 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", DIFFICULTY_DOT[course.difficulty_level as keyof typeof DIFFICULTY_DOT] ?? "bg-muted-foreground")} />
                {difficultyLabel}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  )
}
